import { Alert, AlertIcon, AlertProps, Box, CloseButton, forwardRef, Spinner } from '../../chakra'
import { Close, Info, SuccessO, Warning, WarningO } from '../../../icons'
import { SparkleInfo } from '~/icons/SparkleInfo'

const statusIcons = {
  info: Info,
  error: WarningO,
  warning: Warning,
  success: SuccessO,
  loading: Spinner,
  aiInfo: SparkleInfo
}

export type AlertBannerCustomStatus = NonNullable<AlertProps['status']> | 'aiInfo'

type AlertBannerProps = {
  status: AlertBannerCustomStatus
  hasClose?: boolean
  onClose?: () => void
} & Omit<AlertProps, 'status'>

export const chakraStatusMap: Record<AlertBannerCustomStatus, NonNullable<AlertProps['status']>> = {
  info: 'info',
  error: 'error',
  warning: 'warning',
  success: 'success',
  loading: 'loading',
  aiInfo: 'info'
}

/**
 * Alert banner
 *
 * Alerts are used to communicate a state that affects a system, feature or page.  They can support
 * single-line and multi-line text blocks. This component handles all the styles of the alert container,
 * text styles of the title and description, and custom icons.
 *
 * Statuses include info, success, warning and error.
 *
 * See: https://storybook.beta.vndly.com/?path=/docs/components-alert-banners--docs
 */
export const AlertBanner = forwardRef(
  ({ children, status, hasClose = false, onClose, ...props }: AlertBannerProps, ref) => {
    // Determine the Chakra UI status using the external map
    // Provide a fallback if a status isn't found in the map (e.g., 'info')
    const chakraStatus: NonNullable<AlertProps['status']> = chakraStatusMap[status] || 'info'

    // Get the correct icon based on our custom status
    const IconComponent = statusIcons[status]
    return (
      <Alert
        status={chakraStatus}
        variant={chakraStatus}
        alignItems="flex-start"
        ref={ref}
        data-status={status}
        {...props}>
        <AlertIcon as={IconComponent} boxSize={6} />
        <Box flex="1" data-alert-text data-testid="alert_message">
          {children}
        </Box>
        {hasClose && (
          <CloseButton
            cursor="pointer"
            position="absolute"
            right="8px"
            top="8px"
            w="24px"
            h="24px"
            color="gray.800"
            onClick={onClose}>
            <Close />
          </CloseButton>
        )}
      </Alert>
    )
  }
)

AlertBanner.displayName = 'AlertBanner'
